<script lang="ts">
  import { TRIAL_LENGTHS } from '../../core/balance';
  import { famousFound, medals, twinPairs, visitedCount } from '../../core/engine';
  import { FAMOUS_NUMBERS } from '../../core/famous';
  import { fmt, fmtDuration } from '../../shared/lib/format';
  import { game } from '../../shared/lib/game.svelte';

  const s = $derived(game.s);

  const journey = $derived([
    { label: 'Furthest out', value: fmt(s.maxVisited) },
    { label: 'Deepest down', value: fmt(s.minVisited) },
    { label: 'Integers visited', value: fmt(visitedCount(s)) },
    { label: 'Steps walked', value: fmt(s.steps) },
  ]);
  const haul = $derived([
    { label: 'Prime medals', value: fmt(medals(s)), tone: 'prime' },
    { label: 'Twin pairs', value: fmt(twinPairs(s)), tone: 'twin' },
    {
      label: 'Famous numbers',
      value: `${famousFound(s).length} / ${FAMOUS_NUMBERS.length}`,
      tone: 'famous',
    },
    { label: 'Points earned', value: fmt(s.lifetimePoints), tone: 'accent' },
  ]);
</script>

<div class="view">
  <header class="view-head">
    <h1>Records</h1>
    <p>
      <span class="num">{fmt(s.taps)}</span> taps over
      <span class="num">{fmtDuration(s.playTimeMs)}</span> of play.
    </p>
  </header>

  <section aria-labelledby="r-haul">
    <h2 id="r-haul" class="eyebrow">Collected</h2>
    <dl class="stats">
      {#each haul as h (h.label)}
        <div data-tone={h.tone}>
          <dt>{h.label}</dt>
          <dd class="num">{h.value}</dd>
        </div>
      {/each}
    </dl>
  </section>

  <section aria-labelledby="r-journey">
    <h2 id="r-journey" class="eyebrow">Journey</h2>
    <dl class="stats">
      {#each journey as j (j.label)}
        <div>
          <dt>{j.label}</dt>
          <dd class="num">{j.value}</dd>
        </div>
      {/each}
    </dl>
  </section>

  <section aria-labelledby="r-trials">
    <h2 id="r-trials" class="eyebrow">Speed trial bests</h2>
    <table class="trials">
      <thead><tr><th>Length</th><th>Taps</th><th>Per second</th></tr></thead>
      <tbody>
        {#each TRIAL_LENGTHS as len (len)}
          {@const best = s.trialBests[len]}
          <tr>
            <td>{len} seconds</td>
            <td class="num">{best ? fmt(best) : '—'}</td>
            <td class="num">{best ? (best / len).toFixed(1) : '—'}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </section>
</div>

<style>
  section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .stats {
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 150px), 1fr));
    gap: var(--space-2);
  }
  .stats div {
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius);
    background: var(--bg-raised);
    border: 1px solid var(--line);
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  dt {
    font-size: var(--text-xs);
    color: var(--ink-3);
    font-weight: 600;
  }
  dd {
    margin: 0;
    font-size: var(--text-lg);
    font-weight: 700;
    overflow-wrap: anywhere;
  }
  [data-tone='prime'] dd {
    color: var(--prime);
  }
  [data-tone='twin'] dd {
    color: var(--twin);
  }
  [data-tone='famous'] dd {
    color: var(--famous);
  }
  [data-tone='accent'] dd {
    color: var(--accent);
  }

  .trials {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm);
  }
  .trials th {
    text-align: left;
    font-size: var(--text-xs);
    color: var(--ink-3);
    font-weight: 600;
    padding: 0 var(--space-3) var(--space-2) 0;
    border-bottom: 1px solid var(--line);
  }
  .trials td {
    padding: var(--space-3) var(--space-3) var(--space-3) 0;
    border-bottom: 1px solid var(--line);
  }
  .trials td.num,
  .trials th:not(:first-child) {
    text-align: right;
  }
</style>
